// RecurringMaturityCalculator.jsx
import React, { useState, useCallback, useMemo } from 'react';
import { InputNumber } from 'primereact/inputnumber';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import { Button } from 'primereact/button';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import './RecurringMaturityCalculator.css';

const RecurringMaturityCalculator = () => {
  // State
  const [installment, setInstallment] = useState(5000);
  const [frequency, setFrequency] = useState('Monthly');
  const [termValue, setTermValue] = useState('12');
  const [depositDate, setDepositDate] = useState(new Date());
  const [interestRate, setInterestRate] = useState(7.5);
  const [compoundFrequency, setCompoundFrequency] = useState('Monthly');

  // Frequency options
  const freqOptions = [
    { label: 'Daily', value: 'Daily' },
    { label: 'Weekly', value: 'Weekly' },
    { label: 'Bi-Weekly', value: 'BiWeekly' },
    { label: 'Monthly', value: 'Monthly' },
    { label: 'Quarterly', value: 'Quarterly' },
    { label: 'Half-Yearly', value: 'HalfYearly' },
  ];

  // Compounding frequency options
  const compoundOptions = [
    { label: 'Daily', value: 'Daily' },
    { label: 'Monthly', value: 'Monthly' },
    { label: 'Quarterly', value: 'Quarterly' },
    { label: 'Half-Yearly', value: 'HalfYearly' },
    { label: 'Annually', value: 'Annually' },
  ];

  // Determine the term unit based on frequency
  const getTermUnit = useCallback(() => {
    if (frequency === 'Daily') {
      return 'days';
    } else if (frequency === 'BiWeekly') {
      return 'bi-weekly periods';
    } else if (['Weekly'].includes(frequency)) {
      return 'weeks';
    } else {
      return 'months';
    }
  }, [frequency]);

  const termUnit = getTermUnit();

  // Get term badge text
  const getTermBadge = useCallback(() => {
    if (frequency === 'Daily') {
      return '365 = 1 year';
    } else if (frequency === 'BiWeekly') {
      return '13 = 6 months';
    } else if (frequency === 'Weekly') {
      return '52 = 1 year';
    } else {
      return '12 = 1 year';
    }
  }, [frequency]);

  // Parse term value
  const parseTermValue = useCallback((value) => {
    if (!value || value.trim() === '') return 1;
    const parsed = parseFloat(value);
    return isNaN(parsed) || parsed < 1 ? 1 : parsed;
  }, []);

  // Get months per occurrence for each frequency
  const getMonthsPerOccurrence = useCallback((freq) => {
    switch(freq) {
      case 'Daily': return 1/30.44;
      case 'Weekly': return 1/4.345;
      case 'BiWeekly': return 2/4.345;
      case 'Monthly': return 1;
      case 'Quarterly': return 3;
      case 'HalfYearly': return 6;
      default: return 1;
    }
  }, []);

  // Get days per occurrence for each frequency
  const getDaysPerOccurrence = useCallback((freq) => {
    switch(freq) {
      case 'Daily': return 1;
      case 'Weekly': return 7;
      case 'BiWeekly': return 14;
      case 'Monthly': return 30.44;
      case 'Quarterly': return 91.31;
      case 'HalfYearly': return 182.62;
      default: return 30.44;
    }
  }, []);

  // Get compound periods per year
  const getCompoundPeriodsPerYear = useCallback((freq) => {
    switch(freq) {
      case 'Daily': return 365;
      case 'Monthly': return 12;
      case 'Quarterly': return 4;
      case 'Half-Yearly': return 2;
      case 'Annually': return 1;
      default: return 12;
    }
  }, []);

  // Convert term from one unit to another
  const convertTerm = useCallback((value, fromFreq, toFreq) => {
    const numValue = parseTermValue(value);
    
    // First convert to months (base unit)
    let months;
    switch(fromFreq) {
      case 'Daily':
        months = numValue / 30.44;
        break;
      case 'Weekly':
        months = numValue / 4.345;
        break;
      case 'BiWeekly':
        months = (numValue * 2) / 4.345;
        break;
      default: // Monthly, Quarterly, HalfYearly
        months = numValue;
        break;
    }
    
    // Then convert from months to target frequency unit
    let result;
    switch(toFreq) {
      case 'Daily':
        result = Math.round(months * 30.44);
        break;
      case 'Weekly':
        result = Math.round(months * 4.345);
        break;
      case 'BiWeekly':
        result = Math.round((months * 4.345) / 2);
        break;
      default: // Monthly, Quarterly, HalfYearly
        result = Math.round(months);
        break;
    }
    
    return Math.max(1, result).toString();
  }, [parseTermValue]);

  // Handle frequency change with conversion
  const handleFrequencyChange = useCallback((newFrequency) => {
    // Convert current term value to new frequency unit
    const convertedValue = convertTerm(termValue, frequency, newFrequency);
    setFrequency(newFrequency);
    setTermValue(convertedValue);
  }, [termValue, frequency, convertTerm]);

  // Get term in months
  const getTermInMonths = useCallback(() => {
    const termNum = parseTermValue(termValue);
    
    switch(frequency) {
      case 'Daily':
        return termNum / 30.44;
      case 'Weekly':
        return termNum / 4.345;
      case 'BiWeekly':
        return (termNum * 2) / 4.345;
      default:
        return termNum;
    }
  }, [frequency, termValue, parseTermValue]);

  // Get term in days for date calculation
  const getTermInDays = useCallback(() => {
    const termNum = parseTermValue(termValue);
    
    switch(frequency) {
      case 'Daily':
        return termNum;
      case 'Weekly':
        return termNum * 7;
      case 'BiWeekly':
        return termNum * 14;
      default:
        return termNum * 30.44;
    }
  }, [frequency, termValue, parseTermValue]);

  // Get number of installments
  const getNumberOfInstallments = useCallback(() => {
    const termMonths = getTermInMonths();
    if (termMonths <= 0) return 0;

    const monthsPerOccurrence = getMonthsPerOccurrence(frequency);
    let count = Math.round(termMonths / monthsPerOccurrence);
    return count < 1 ? 1 : count;
  }, [frequency, getTermInMonths, getMonthsPerOccurrence]);

  // Calculate maturity amount with compound interest
  const calculateMaturityWithInterest = useCallback(() => {
    const termMonths = getTermInMonths();
    const numInstallments = getNumberOfInstallments();
    const monthsPerInstallment = getMonthsPerOccurrence(frequency);
    
    if (termMonths <= 0 || numInstallments <= 0 || installment <= 0) {
      return { maturityAmount: 0, totalInvested: 0, interestEarned: 0 };
    }

    const annualRate = interestRate / 100;
    const periodsPerYear = getCompoundPeriodsPerYear(compoundFrequency);
    const ratePerPeriod = annualRate / periodsPerYear;
    
    const periodsPerInstallment = (monthsPerInstallment / 12) * periodsPerYear;
    
    let maturityAmount = 0;
    let totalInvested = 0;
    
    for (let i = 0; i < numInstallments; i++) {
      const installmentsRemaining = numInstallments - i;
      const periodsRemaining = installmentsRemaining * periodsPerInstallment;
      
      let futureValue;
      if (periodsRemaining > 0 && ratePerPeriod > 0) {
        futureValue = installment * Math.pow(1 + ratePerPeriod, periodsRemaining);
      } else {
        futureValue = installment;
      }
      
      maturityAmount += futureValue;
      totalInvested += installment;
    }
    
    const interestEarned = maturityAmount - totalInvested;
    
    return {
      maturityAmount: Math.round(maturityAmount),
      totalInvested: Math.round(totalInvested),
      interestEarned: Math.round(interestEarned)
    };
  }, [installment, interestRate, frequency, compoundFrequency, getTermInMonths, getNumberOfInstallments, getMonthsPerOccurrence, getCompoundPeriodsPerYear]);

  // Compute maturity
  const computeMaturity = useCallback(() => {
    const termNum = parseTermValue(termValue);
    if (!depositDate || !termNum || termNum <= 0) {
      return { 
        maturityDate: null, 
        totalInvested: 0, 
        numberOfInstallments: 0, 
        termMonths: 0,
        termDisplay: 0,
        maturityAmount: 0,
        interestEarned: 0
      };
    }

    const termMonths = getTermInMonths();
    const numInstallments = getNumberOfInstallments();
    const { maturityAmount, totalInvested, interestEarned } = calculateMaturityWithInterest();
    const termDays = getTermInDays();

    const matDate = new Date(depositDate);
    matDate.setDate(matDate.getDate() + Math.round(termDays));

    let termDisplay = termNum;
    let displayUnit = termUnit;

    return {
      maturityDate: matDate,
      totalInvested,
      numberOfInstallments: numInstallments,
      termMonths: Math.round(termMonths * 100) / 100,
      termDisplay: termDisplay,
      displayUnit: displayUnit,
      maturityAmount,
      interestEarned,
      termDays: Math.round(termDays)
    };
  }, [depositDate, termValue, frequency, installment, getTermInMonths, getNumberOfInstallments, parseTermValue, calculateMaturityWithInterest, getTermInDays, termUnit]);

  const { 
    maturityDate, 
    totalInvested, 
    numberOfInstallments, 
    termMonths, 
    maturityAmount, 
    interestEarned,
    termDisplay,
    displayUnit,
    termDays
  } = computeMaturity();

  // Helpers
  const formatCurrency = (value) => {
    if (value === undefined || value === null) return '0';
    return value.toLocaleString('en-IN', { maximumFractionDigits: 0, style: 'currency', currency: 'INR' });
  };

  const formatDate = (date) => {
    if (!date) return '—';
    return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  // Handle term input change
  const handleTermChange = (e) => {
    const value = e.target.value;
    if (value === '' || /^\d*\.?\d*$/.test(value)) {
      setTermValue(value);
    }
  };

  // Quick term presets based on frequency
  const getPresets = useCallback(() => {
    if (frequency === 'Daily') {
      return [
        { label: '30 (1 mo)', value: '30' },
        { label: '90 (3 mo)', value: '90' },
        { label: '180 (6 mo)', value: '180' },
        { label: '365 (1 yr)', value: '365' },
        { label: '730 (2 yr)', value: '730' },
        { label: '1095 (3 yr)', value: '1095' },
        { label: '1825 (5 yr)', value: '1825' },
      ];
    } else if (frequency === 'BiWeekly') {
      return [
        { label: '13 (6 mo)', value: '13' },
        { label: '26 (1 yr)', value: '26' },
        { label: '52 (2 yr)', value: '52' },
        { label: '78 (3 yr)', value: '78' },
        { label: '130 (5 yr)', value: '130' },
      ];
    } else if (frequency === 'Weekly') {
      return [
        { label: '13 (3 mo)', value: '13' },
        { label: '26 (6 mo)', value: '26' },
        { label: '44 (10 mo)', value: '44' },
        { label: '52 (1 yr)', value: '52' },
        { label: '104 (2 yr)', value: '104' },
        { label: '156 (3 yr)', value: '156' },
        { label: '260 (5 yr)', value: '260' },
      ];
    } else {
      return [
        { label: '3 (3 mo)', value: '3' },
        { label: '6 (6 mo)', value: '6' },
        { label: '9 (9 mo)', value: '9' },
        { label: '12 (1 yr)', value: '12' },
        { label: '24 (2 yr)', value: '24' },
        { label: '36 (3 yr)', value: '36' },
        { label: '60 (5 yr)', value: '60' },
      ];
    }
  }, [frequency]);

  return (
    <div className="calculator-card">
      <h1>
        <i className="pi pi-calendar-plus"></i>
        Recurring Maturity Calculator
      </h1>
      <div className="subhead">
        Calculate maturity amount &amp; date · Enter any term value (30 days, 52 weeks, 12 months, etc.)
      </div>

      <div className="field-grid">
        {/* Installment Amount */}
        <div className="field-full">
          <div className="label-with-icon">
            <i className="pi pi-wallet"></i> Unit / Installment Amount
          </div>
          <InputNumber
            value={installment}
            onValueChange={(e) => setInstallment(e.value || 0)}
            mode="currency"
            currency="INR"
            locale="en-IN"
            min={0}
            step={100}
            style={{ width: '100%' }}
          />
        </div>

        {/* Interest Rate */}
        <div>
          <div className="label-with-icon">
            <i className="pi pi-chart-line"></i> Interest Rate (p.a.)
          </div>
          <InputNumber
            value={interestRate}
            onValueChange={(e) => setInterestRate(e.value || 0)}
            mode="decimal"
            min={0}
            max={100}
            step={0.25}
            suffix=" %"
            style={{ width: '100%' }}
          />
        </div>

        {/* Compounding Frequency */}
        <div>
          <div className="label-with-icon">
            <i className="pi pi-sync"></i> Compounding Frequency
          </div>
          <Dropdown
            value={compoundFrequency}
            options={compoundOptions}
            onChange={(e) => setCompoundFrequency(e.value)}
            placeholder="Select Compounding"
            style={{ width: '100%' }}
          />
        </div>

        {/* Frequency */}
        <div>
          <div className="label-with-icon">
            <i className="pi pi-clock"></i> Deposit Frequency
          </div>
          <Dropdown
            value={frequency}
            options={freqOptions}
            onChange={(e) => handleFrequencyChange(e.value)}
            placeholder="Select Frequency"
            style={{ width: '100%' }}
          />
        </div>

        {/* Term - Text Input */}
        <div className="field-full">
          <div className="label-with-icon">
            <i className="pi pi-calendar"></i> Term
            <span className="term-unit-badge">
              <i className="pi pi-info-circle"></i> {getTermBadge()}
            </span>
          </div>
          <div className="term-input-wrapper">
            <InputText
              value={termValue}
              onChange={handleTermChange}
              placeholder={`Enter term in ${termUnit}`}
              className="term-input"
            />
            <span className="term-unit-label">{termUnit}</span>
          </div>
          <div className="term-hint">
            {getPresets().map((preset) => (
              <Button
                key={preset.value}
                label={preset.label}
                size="small"
                text
                onClick={() => setTermValue(preset.value)}
              />
            ))}
          </div>
        </div>

        {/* Deposit Date */}
        <div className="field-full">
          <div className="label-with-icon">
            <i className="pi pi-calendar-minus"></i> Deposit Start Date
          </div>
          <Calendar
            value={depositDate}
            onChange={(e) => setDepositDate(e.value)}
            showIcon
            dateFormat="dd/mm/yy"
            style={{ width: '100%' }}
            minDate={new Date()}
          />
        </div>
      </div>

      {/* Results */}
      <div className="result-panel">
        <div className="result-item">
          <span className="result-label">Maturity Amount</span>
          <span className="result-value" style={{ fontSize: '1.8rem', color: '#059669' }}>
            {formatCurrency(maturityAmount || totalInvested)}
          </span>
        </div>
        
        <div className="result-grid">
          <div className="result-item">
            <span className="result-label">Total Invested</span>
            <span className="result-value" style={{ fontSize: '1.1rem' }}>
              {formatCurrency(totalInvested)}
            </span>
          </div>
          <div className="result-item">
            <span className="result-label">Interest Earned</span>
            <span className="result-value" style={{ fontSize: '1.1rem', color: interestEarned > 0 ? '#059669' : '#6b7280' }}>
              {formatCurrency(interestEarned)}
            </span>
          </div>
        </div>
        
        <div className="result-item">
          <span className="result-label">Maturity Date</span>
          <span className="maturity-date-value">
            {maturityDate ? formatDate(maturityDate) : '—'}
          </span>
        </div>
        
        <div className="result-item" style={{ alignItems: 'flex-end' }}>
          <span className="result-label">Total Installments</span>
          <span style={{ fontSize: '1.3rem', fontWeight: 500, color: '#0f172a' }}>
            {numberOfInstallments || 0}
          </span>
        </div>
      </div>

      {/* Info Chip */}
      <div className="info-chip">
        <span><i className="pi pi-tag"></i> Frequency: <strong>{frequency}</strong></span>
        <span><i className="pi pi-percentage"></i> Rate: <strong>{interestRate}% p.a.</strong></span>
        <span><i className="pi pi-sync"></i> Compounding: <strong>{compoundFrequency}</strong></span>
        <span><i className="pi pi-clock"></i> Term: <strong>{termDisplay || '—'} {displayUnit}</strong></span>
        <span><i className="pi pi-calendar"></i> Start: <strong>{depositDate ? formatDate(depositDate) : '—'}</strong></span>
        <span><i className="pi pi-clock"></i> Equivalent: <strong>{termMonths} months ({termDays} days)</strong></span>
      </div>

      {/* Actions */}
      <div className="footer-actions">
        <Button
          label="Reset"
          icon="pi pi-refresh"
          outlined
          onClick={() => {
            setInstallment(5000);
            setFrequency('Monthly');
            setTermValue('12');
            setDepositDate(new Date());
            setInterestRate(7.5);
            setCompoundFrequency('Monthly');
          }}
        />
        <Button
          label="Recalculate"
          icon="pi pi-calculator"
          onClick={() => {}}
        />
      </div>

      <div className="footer-note">
        <i className="pi pi-info-circle"></i> 
        Enter any term value: 30 days, 52 weeks, 13 bi-weekly periods, or any custom number.
        <strong> When you change frequency, the term value automatically converts!</strong>
      </div>
    </div>
  );
};

export default RecurringMaturityCalculator;